'use client';

import React, { Suspense, useCallback, useState } from 'react';
import { useAuthManagementController } from '@/features/auth-management/model/hooks/use-auth-management-controller';
import { AuthUrlInitializer } from '@/features/auth-management/model/hooks/auth-url-initializer';
import { useAuthorization } from '@/entities/session/model/hooks';
import { useHeaderEnabled } from '@/shared/providers/fullscreen-provider/store';
import { useVisibleState } from '@/shared/hooks/useVisibleState';
import { useIsAuth } from '@/shared/providers/user-provider/use-user';
import { ContentWrapperLayout } from './styles';
import { LayoutContent } from './layout-content';
import { ResponsiveLayoutFrame } from './layout-frame';
import {
  AppShellQueryParamsInitializer,
  type WalletInitialStep,
} from './app-shell-query-params-initializer';
import {
  AppShellConfigProvider,
  useAppShellConfig,
} from './app-shell-config-context';

interface AppShellProps {
  children: React.ReactNode;
  footer: React.ReactNode;
  header: React.ReactNode;
}

interface AppShellContentWrapperProps {
  children: React.ReactNode;
}

const AppShellContentWrapper: React.FC<AppShellContentWrapperProps> = ({
  children,
}) => {
  return <ContentWrapperLayout>{children}</ContentWrapperLayout>;
};

const AppShellContent: React.FC<AppShellProps> = ({
  children,
  footer,
  header,
}) => {
  const { flexMain, hideFooter, withoutMobileMenuPadding } =
    useAppShellConfig();
  const { emailCodeAuthenticate } = useAuthorization();

  const isAuth = useIsAuth();
  const headerEnabled = useHeaderEnabled();

  const {
    open: openWalletManagement,
    visible: visibleWalletManagement,
    close: closeWalletManagement,
  } = useVisibleState(false);

  const {
    open: openCashbackModal,
    visible: visibleCashbackModal,
    close: closeCashbackModal,
  } = useVisibleState(false);

  const {
    open: openAuthManagement,
    visible: visibleAuthManagement,
    onClose: closeAuthManagement,
  } = useAuthManagementController();
  const [walletInitialStep, setWalletInitialStep] =
    useState<WalletInitialStep>('deposit');
  const resetWalletInitialStep = useCallback(
    () => setWalletInitialStep('deposit'),
    []
  );

  const content = (
    <LayoutContent
      mode={isAuth ? 'auth' : 'base'}
      authManagement={{
        onOpen: openAuthManagement,
        onClose: closeAuthManagement,
        visible: visibleAuthManagement,
      }}
      walletManagement={{
        initialStep: walletInitialStep,
        onOpen: openWalletManagement,
        onClose: () => {
          closeWalletManagement();
          resetWalletInitialStep();
        },
        visible: visibleWalletManagement,
      }}
      cashbackModal={{
        onOpen: openCashbackModal,
        visible: visibleCashbackModal,
        onClose: closeCashbackModal,
      }}
      flexMain={flexMain}
      footer={footer}
      header={header}
      headerEnabled={headerEnabled}
      hideFooter={hideFooter}
    >
      <Suspense fallback={null}>
        <AuthUrlInitializer onOpen={openAuthManagement} />
        <AppShellQueryParamsInitializer
          emailCodeAuthenticate={emailCodeAuthenticate}
          isAuth={isAuth}
          onAuthManagementOpen={openAuthManagement}
          onWalletInitialStepChange={setWalletInitialStep}
          onWalletOpen={openWalletManagement}
        />
      </Suspense>
      <AppShellContentWrapper>{children}</AppShellContentWrapper>
    </LayoutContent>
  );

  return (
    <ResponsiveLayoutFrame
      flexMain={flexMain}
      withoutMobileMenuPadding={withoutMobileMenuPadding}
    >
      {content}
    </ResponsiveLayoutFrame>
  );
};

export const AppShell: React.FC<AppShellProps> = ({
  children,
  footer,
  header,
}) => {
  return (
    <AppShellConfigProvider>
      <AppShellContent footer={footer} header={header}>
        {children}
      </AppShellContent>
    </AppShellConfigProvider>
  );
};
